import { some } from 'lodash'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import Button from '@/components/Button'
import Card from '@/components/Card'
import CatalogAppInstanceForm from '@/components/CatalogAppInstanceForm'
import { ImagePreview } from '@/components/ImagePreview'
import Tabs from '@/components/Tabs'
import TwoColumnLayout from '@/components/TwoColumnLayout'
import { CatalogAppDetails, SpaceCatalogAppDetailsProps } from '@/pages/SpaceCatalogAppDetailsPage/interfaces'

import ExternalLink from './ExternalLink'
import messages from './messages'

const DocumentationLinkStyled = styled(ExternalLink)`
  position: absolute;
  right: 0px;
  margin-right: 2rem;
`

export type SpaceCatalogAppDetailsDescriptionProps = SpaceCatalogAppDetailsProps<CatalogAppDetails>

const TagGroupTitle = styled.div`
  font-weight: 800;
  font-size: 0.9em;
  margin-top: 0.5em;
  margin-bottom: 0.75em;
`

const CardStyled = styled(Card)`
  .column-left {
    position: relative;
    bottom: 2rem;
  }

  .icon-preview-upload {
    min-width: 6.125rem;
    max-width: 6.125rem;
  }
`
const HeroImageWrapper = styled.figure`
  margin: 0;
  padding-top: 0.25rem;

  @media (min-width: 720px) {
    padding-top: 1rem;
  }
`

type TagsGroupProps = {
  title: string
  tags: string[]
}

const TagsGroup = (props: TagsGroupProps) => {
  return (
    <div className="flex">
      <TagGroupTitle>{props.title}</TagGroupTitle>
      {props.tags.map(tag => (
        <div className="category-tag" key={tag}>
          {tag}
        </div>
      ))}
    </div>
  )
}

const anyTagsPresent = (tags: string[]) => some(tags, tag => tag !== '' && tag !== null && tag !== undefined)

const SpaceCatalogAppDetailsDescription = ({
  app,
  deleteApp,
  goBack = () => null,
  goToConnectorLogs = () => null,
  installApp,
  installView = false,
  toggleInstallView = () => null,
  updateApp,
  isDeleteActionInProgress,
  isUpdateActionInProgress
}: SpaceCatalogAppDetailsDescriptionProps) => {
  const intl = useIntl()

  const iconUrl = app ? app.iconUrl : ''
  const [getActionMessage] = useState(() => {
    if (isDeleteActionInProgress) {
      return <FormattedMessage {...messages.deleting} />
    } else if (isUpdateActionInProgress) {
      return <FormattedMessage {...messages.saving} />
    }
  })

  return installView ? (
    <CatalogAppInstanceForm
      appNameOverride={null}
      actionMessage={getActionMessage}
      onDelete={deleteApp}
      isActionInProgress={isDeleteActionInProgress || isUpdateActionInProgress}
      app={app}
      appImageUrlOverride={iconUrl}
      isUpdateMode={false}
      goBack={goBack}
      goToConnectorLogs={goToConnectorLogs}
      onInstall={installApp}
      onUpdate={updateApp}
      webHookUrl={app.webHookUrl}
    />
  ) : (
    <CardStyled>
      {app ? (
        <>
          <Card.Content>
            {app.referenceDocumentationUrl && (
              <DocumentationLinkStyled href={app.referenceDocumentationUrl}>
                <FormattedMessage {...messages.documentationLink} />
              </DocumentationLinkStyled>
            )}
            <TwoColumnLayout
              leftColumn={
                <div>
                  <ImagePreview className="icon-preview-upload" src={iconUrl} />
                  {anyTagsPresent(app.category) && (
                    <TagsGroup title={intl.formatMessage(messages.categories)} tags={app.category}></TagsGroup>
                  )}
                  {anyTagsPresent(app.features) && (
                    <TagsGroup title={intl.formatMessage(messages.features)} tags={app.features}></TagsGroup>
                  )}
                  {anyTagsPresent(app.integrations) && (
                    <TagsGroup title={intl.formatMessage(messages.integrations)} tags={app.integrations}></TagsGroup>
                  )}
                </div>
              }
              rightColumn={
                <Tabs>
                  <div label={intl.formatMessage(messages.overview)}>
                    <div className="app-details-overview">{app.description}</div>
                    {app.heroImageUrl && (
                      <HeroImageWrapper>
                        <img className="base-image" src={app.heroImageUrl} alt={app.name} />
                      </HeroImageWrapper>
                    )}
                  </div>
                  <div label={intl.formatMessage(messages.settingsInfo)}>
                    <h3 className="app-functionality-header">
                      {<FormattedMessage {...messages.functionalityFeaturesSettings} />}
                    </h3>
                    {app.isMicroAppAvailable && (
                      <div className="app-settings-section">
                        <div className="audience-targeting-logo" />
                        <h3>{<FormattedMessage {...messages.audienceTargetingSettings} />}</h3>
                        <div className="app-details-text">
                          {<FormattedMessage {...messages.audienceTargetingSettingsDescription} />}
                        </div>
                      </div>
                    )}
                    {app.isConnectorAvailable && (
                      <div className="app-settings-section">
                        <div className="smart-notifications-logo" />
                        <h3>{<FormattedMessage {...messages.smartNotifications} />}</h3>
                        <div className="app-details-text">
                          {<FormattedMessage {...messages.smartNotificationsDescription} />}
                        </div>
                      </div>
                    )}
                    {app.isMicroAppAvailable && (
                      <div className="app-settings-section">
                        <div className="user-interface-logo" />
                        <h3>{<FormattedMessage {...messages.appUserInterface} />}</h3>
                        <div className="app-details-text">
                          {<FormattedMessage {...messages.appUserInterfaceDescription} />}
                        </div>
                      </div>
                    )}
                    {!app.isConnectorAvailable && !app.isMicroAppAvailable && (
                      <div className="app-details-text">{<FormattedMessage {...messages.noSettingsInfo} />}</div>
                    )}
                  </div>
                </Tabs>
              }
              title={app.name}
            />
          </Card.Content>

          <Card.Footer>
            {!installView && (
              <>
                <Button onClick={goBack} color={'secondary'} className="app-details-button">
                  <FormattedMessage {...messages.cancel} />
                </Button>
                <Button onClick={toggleInstallView} color={'primary'} className="app-details-button">
                  <FormattedMessage {...messages.add} />
                </Button>
              </>
            )}
          </Card.Footer>
        </>
      ) : (
        <Card.Content>
          <TwoColumnLayout
            leftColumn={<ImagePreview className="icon-preview-upload" src="" />}
            rightColumn={<FormattedMessage {...messages.unableToLoad} />}
            title={<FormattedMessage {...messages.unableToLoadHeader} />}
          />
        </Card.Content>
      )}
    </CardStyled>
  )
}

SpaceCatalogAppDetailsDescription.displayName = 'SpaceCatalogAppDetailsDescription'

export default SpaceCatalogAppDetailsDescription
